import type { StationAnalyticsSnapshot } from "../../shared/types";
import { ApiRequestError, getRequired } from "./apiClient";

interface StationAnalyticsRequest {
  stationSks: number[];
  period: "ytd";
}

interface StationAnalyticsCacheEntry {
  expiresAt: number;
  snapshot: StationAnalyticsSnapshot;
}

const stationAnalyticsCache = new Map<string, StationAnalyticsCacheEntry>();
const stationAnalyticsInFlight = new Map<string, Promise<StationAnalyticsSnapshot>>();
const stationAnalyticsCacheTtlMs = 30 * 1000;

function normalizeStationSks(stationSks: number[]): number[] {
  return [...new Set(stationSks
    .map((stationSk) => Number(stationSk))
    .filter((stationSk) => Number.isFinite(stationSk))
    .map((stationSk) => Math.trunc(stationSk)))]
    .sort((left, right) => left - right);
}

function stationAnalyticsCacheKey(stationSks: number[], period: "ytd"): string {
  return `${period}:${normalizeStationSks(stationSks).join(",")}`;
}

function buildQuery(params: StationAnalyticsRequest): string {
  const searchParams = new URLSearchParams();
  searchParams.set("period", params.period);
  const stationSks = normalizeStationSks(params.stationSks);
  if (stationSks.length > 0) {
    searchParams.set("stationSks", stationSks.join(","));
  }

  return searchParams.toString();
}

export function readCachedStationAnalytics(stationSks: number[], period: "ytd" = "ytd"): StationAnalyticsSnapshot | undefined {
  const entry = stationAnalyticsCache.get(stationAnalyticsCacheKey(stationSks, period));
  if (!entry || entry.expiresAt <= Date.now()) {
    return undefined;
  }

  return entry.snapshot;
}

export function getStationAnalytics(
  stationSks: number[],
  period: "ytd" = "ytd",
  options: { forceRefresh?: boolean } = {}
): Promise<StationAnalyticsSnapshot> {
  const key = stationAnalyticsCacheKey(stationSks, period);
  const cached = readCachedStationAnalytics(stationSks, period);
  if (!options.forceRefresh && cached) {
    return Promise.resolve(cached);
  }

  const existing = stationAnalyticsInFlight.get(key);
  if (existing) {
    return existing;
  }

  const query = buildQuery({ stationSks, period });
  const promise = getRequired<StationAnalyticsSnapshot>(`/stations/analytics?${query}`)
    .then((snapshot) => {
      stationAnalyticsCache.set(key, {
        expiresAt: Date.now() + stationAnalyticsCacheTtlMs,
        snapshot
      });
      return snapshot;
    })
    .catch((error: unknown) => {
      const saved = stationAnalyticsCache.get(key)?.snapshot;
      const transient = error instanceof TypeError || (error instanceof ApiRequestError && (error.status === 429 || error.status >= 500));
      if (saved?.delivery && transient) {
        return { ...saved, delivery: { ...saved.delivery, status: "stale" as const } };
      }
      throw error;
    })
    .finally(() => {
      stationAnalyticsInFlight.delete(key);
    });

  stationAnalyticsInFlight.set(key, promise);
  return promise;
}

export function prefetchStationAnalytics(stationSks: number[], period: "ytd" = "ytd"): Promise<StationAnalyticsSnapshot | undefined> | undefined {
  if (readCachedStationAnalytics(stationSks, period)) {
    return undefined;
  }

  return getStationAnalytics(stationSks, period).catch((error) => {
    console.warn("Station analytics prefetch failed", error);
    return undefined;
  });
}
